// Presencia: le avisa al backend si el usuario está MIRANDO la app en este momento.
//
// Para qué: para no mandarle un push de "tu generación está lista" a alguien que está viendo la
// pantalla (ya lo ve ahí). Spec: docs/2026-08-31-push-al-terminar-generacion-spec.md.
//
// Por qué el estado y no "hace cuánto estuvo activo": las generaciones tardan entre 30 segundos y
// 4 minutos, así que cualquier umbral temporal falla en algún rango. El caso que rompe es justo el
// que la feature quiere cubrir — el usuario se va a los 10s, la imagen tarda 40, y un umbral de 2
// minutos diría "estuvo activo recién" y no le mandaría nada.
//
// Best-effort TOTAL, como `signupAttribution`: nunca lanza. El peor efecto de que falle es un push
// de más, y eso jamás puede romperle la navegación a nadie.

import { supabase } from './supabase';
import { ATLAS_BACKEND_URL } from '../config';

/** Cada cuánto se manda el "sigo acá" mientras la pestaña está visible. */
export const HEARTBEAT_MS = 60_000;

/**
 * Id de ESTA pestaña. El backend guarda una fila de presencia por (usuario, pestaña).
 *
 * Sin esto, la misma cuenta abierta en la compu y en el celular compartía una sola fila y la última
 * en reportar pisaba a la otra: el celular con la pestaña en segundo plano marcaba "no estoy
 * mirando" y le llegaba el push a alguien que tenía la app abierta adelante.
 *
 * Vive SÓLO en memoria, sin `sessionStorage`. La primera versión lo guardaba ahí para que
 * sobreviviera a un F5, pero **duplicar una pestaña copia el `sessionStorage`**, así que las dos
 * copias quedaban con el mismo id y volvía el mismo bug en chico: la copia que se oculta pisaba el
 * estado de la que se está mirando (hallazgo de Greptile, PR #83). En memoria eso es imposible:
 * cada pestaña arranca con el suyo, siempre.
 *
 * Perder el id al recargar no cuesta nada: al recargar se dispara `pagehide`, que deja la fila
 * anterior en "no estoy mirando", y la fila huérfana la limpia la poda del backend.
 *
 * Es un id opaco y descartable: no identifica a nadie ni sobrevive a la pestaña.
 */
const CLIENT_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `c-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

export function obtenerClientId(): string {
  return CLIENT_ID;
}

/**
 * Número de reporte de esta pestaña. Sube de a uno y viaja en cada aviso.
 *
 * Los reportes salen sin esperar respuesta, así que dos que salen casi juntos pueden llegar al
 * servidor **al revés**. El caso caro: el latido "sigo acá" todavía viajando cuando la persona
 * cierra la pestaña y sale el "me fui". Si gana el latido viejo, esa pestaña queda marcada como
 * mirando y el barrido **saltea el push** — el aviso se pierde en silencio, que es justo lo que la
 * feature promete no hacer. El backend descarta el reporte cuyo `seq` no sea mayor al que ya tiene
 * (hallazgo de Greptile, PR #83).
 *
 * Es un contador, no un reloj: no depende de que la hora de la máquina sea correcta, y sólo se
 * compara contra reportes de esta misma pestaña.
 */
let seq = 0;

/**
 * Último token conocido. Existe por dos razones, las dos de la review del PR #83:
 *
 * 1. **El aviso de cierre no puede esperar nada.** `keepalive` garantiza que un request YA INICIADO
 *    sobreviva a la descarga del documento — no que uno que todavía no arrancó llegue a salir. Si
 *    en `pagehide` hubiera que esperar a `getSession()` (que puede ir a la red a refrescar el
 *    token), la página se descarga antes y el "me fui" **no sale nunca**: la pestaña queda marcada
 *    como mirando hasta que venza el latido, y en esos 90s se pierde el push.
 * 2. **Al cerrar sesión hay que avisar con el token viejo.** Después del `SIGNED_OUT` ya no hay
 *    sesión que pedir, así que sin esto la fila quedaba en "mirando" hasta vencer.
 */
let tokenCacheado: string | null = null;

/**
 * Guarda (o borra) el token conocido. La llama el hook con lo que trae `onAuthStateChange`.
 *
 * Es importante que el token venga de AFUERA y no de un `getSession()` adentro de ese callback:
 * llamar a la API de auth dentro de su propio callback puede trabar a supabase-js.
 */
export function recordarToken(token: string | null): void {
  tokenCacheado = token;
}

async function tokenDeLaSesion(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Arma el request. Compartido por el camino normal y el de cierre, así no se desincronizan.
 *
 * `nro` viene de afuera **a propósito**: se toma cuando pasó el evento, no acá. Tomarlo en este
 * punto haría que reflejara el orden en que se resolvieron las promesas en vez del orden real de
 * los hechos, que es justo lo que el contador existe para arreglar.
 */
function mandar(mirando: boolean, token: string, keepalive: boolean, nro: number): void {
  void fetch(`${ATLAS_BACKEND_URL}/v1/presence`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ mirando, client_id: CLIENT_ID, seq: nro }),
    keepalive,
  }).catch(() => {
    /* best-effort: sin red, sin backend o con la página cerrándose, no pasa nada */
  });
}

/**
 * Reporta el estado. Camino normal (montaje, cambio de visibilidad, latido).
 *
 * `token` se pasa cuando ya se tiene a mano —el callback de `onAuthStateChange` lo trae— para no
 * pedirle la sesión a supabase adentro de su propio callback.
 *
 * NO se usa `sendBeacon` aunque sea lo típico para esto: no admite headers, así que el JWT tendría
 * que viajar en la query string y terminaría escrito en los logs de acceso del servidor. `fetch`
 * con `keepalive` da la misma garantía y lo manda en el header, como el resto de la app.
 */
export async function reportarPresencia(
  mirando: boolean,
  opts?: { token?: string | null },
): Promise<void> {
  // El número se toma ACÁ, antes de cualquier `await`: tiene que reflejar el orden en que pasaron
  // las cosas en el navegador, no el orden en que terminó de resolverse la sesión.
  const nro = ++seq;
  try {
    // Si el llamador ya tiene el token, se usa ese y NO se le pide la sesión a supabase.
    const token = opts && 'token' in opts ? opts.token : await tokenDeLaSesion();
    if (!token) return; // sin sesión no hay a quién avisarle
    tokenCacheado = token;
    mandar(mirando, token, false, nro);
  } catch {
    /* best-effort: nunca rompe nada */
  }
}

/**
 * ¿Este evento de auth deja colgada la presencia del usuario anterior?
 *
 * Los dos casos son el mismo problema: alguien deja de estar y su fila queda en "mirando" hasta
 * vencer, y en esa ventana el barrido le saltea el push.
 *
 * 1. `SIGNED_OUT` — el obvio: cerró sesión.
 * 2. **Cambio de cuenta sin cerrar sesión** — un `SIGNED_IN` de OTRA persona encima de la sesión
 *    que había, sin `SIGNED_OUT` en el medio. El token se reemplaza y el usuario que se fue queda
 *    marcado como presente (hallazgo de Greptile, PR #83).
 *
 * Un `TOKEN_REFRESHED` o un `USER_UPDATED` de la misma persona NO cuentan: es la misma sesión.
 */
export function seFueElUsuarioAnterior(
  evento: string,
  usuarioAnterior: string | null,
  usuarioNuevo: string | null,
): boolean {
  if (usuarioAnterior === null) return false; // no había nadie de quien despedirse
  if (evento === 'SIGNED_OUT') return true;
  return usuarioNuevo !== null && usuarioNuevo !== usuarioAnterior;
}

/**
 * Avisa "me fui". **Síncrona a propósito**: se llama desde `pagehide` y al cerrar sesión, y en esos
 * momentos no hay tiempo para ningún `await` — el request tiene que estar iniciado antes de que el
 * documento se descargue, que es lo único que `keepalive` garantiza.
 *
 * Por eso usa el token en memoria en vez de pedirlo. Si no hay ninguno, no hace nada: sin sesión
 * previa tampoco hay fila de presencia que corregir.
 */
export function reportarSalida(): void {
  try {
    if (!tokenCacheado) return;
    mandar(false, tokenCacheado, true, ++seq);
  } catch {
    /* best-effort: nunca rompe nada */
  }
}
